var __assign = (this && this.__assign) || function () {
    __assign = Object.assign || function(t) {
        for (var s, i = 1, n = arguments.length; i < n; i++) {
            s = arguments[i];
            for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p))
                t[p] = s[p];
        }
        return t;
    };
    return __assign.apply(this, arguments);
};
import { useCallback, useEffect, useMemo } from 'react';
import { useDataProvider, useEvent, useSafeSetState } from 'react-admin';
import { useQuery, keepPreviousData, } from '@tanstack/react-query';
/**
 * Get a callback to fetch global search results from the data provider using its `search` method.
 *
 * The request starts when the callback is called.
 *
 * useSearch() parameters can be passed:
 *
 * - at definition time
 *
 *       const [search] = useSearch({ options: { targets: ['artists', 'albums'] } });
 *       search(query);
 *
 * - at call time
 *
 *       const [search] = useSearch();
 *       search(query, { targets: ['artists', 'albums'] });
 *
 * - both, in which case the definition and call time parameters are merged
 *
 *       const [search] = useSearch({ options: { targets: ['artists', 'albums'] } });
 *       search(query, { targets: ['concerts'] });
 *
 * @param {UseSearchOptions} props
 * - {Object} options: Optional. The options used to call the data provider's `search` method.
 * - {Object} queryOptions: Optional. The options used to call the `useQuery` hook.
 *
 * @returns A tuple with the search callback and the request state. Destructure as [search, { query, data, total, error, isPending, isFetched }].
 *
 * The return value updates according to the request state:
 *
 * - mount:         [search, { isPending: true, isFetched: false }]
 * - search called: [search, { isPending: true, isFetched: false }]
 * - success:       [search, { query: [string], data: [data from response], total: [total from response], isPending: false, isFetched: true }]
 * - error:         [search, { query: [string], error: [error from response], isPending: false, isFetched: true }]
 *
 * The search function accepts the following arguments:
 * - {String} query: The terms to search for
 * - {Object} options
 *   - {String[]} targets: Optional. The indices for which the search should be performed.
 */
export var useSearch = function (props) {
    if (props === void 0) { props = {}; }
    var defaultOptions = props.options, _a = props.queryOptions, queryOptions = _a === void 0 ? {} : _a, _b = props.disableHighlight, disableHighlight = _b === void 0 ? false : _b;
    var _c = useSafeSetState(false), runQuery = _c[0], setRunQuery = _c[1];
    var _d = useSafeSetState({
        query: '',
        options: {},
    }), searchParams = _d[0], setSearchParams = _d[1];
    var dataProvider = useDataProvider();
    var _e = searchParams !== null && searchParams !== void 0 ? searchParams : {}, _f = _e.query, query = _f === void 0 ? '' : _f, _g = _e.options, options = _g === void 0 ? {} : _g;
    var onSuccess = queryOptions.onSuccess, onError = queryOptions.onError, onSettled = queryOptions.onSettled, meta = queryOptions.meta;
    var searchOptions = __assign(__assign(__assign({}, defaultOptions), options), { meta: meta });
    var onSuccessEvent = useEvent(onSuccess !== null && onSuccess !== void 0 ? onSuccess : (function () { return undefined; }));
    var onErrorEvent = useEvent(onError !== null && onError !== void 0 ? onError : (function () { return undefined; }));
    var onSettledEvent = useEvent(onSettled !== null && onSettled !== void 0 ? onSettled : (function () { return undefined; }));
    var queryResult = useQuery(__assign({ queryKey: ['search', query, searchOptions], queryFn: function () { return dataProvider.search(query, searchOptions); }, enabled: runQuery, placeholderData: keepPreviousData }, (queryOptions || {})));
    useEffect(function () {
        if (queryResult.data === undefined ||
            queryResult.error != null ||
            queryResult.isFetching)
            return;
        onSuccessEvent(queryResult.data);
    }, [
        onSuccessEvent,
        queryResult.data,
        queryResult.error,
        queryResult.isFetching,
    ]);
    useEffect(function () {
        if (queryResult.error == null || queryResult.isFetching)
            return;
        onErrorEvent(queryResult.error);
    }, [onErrorEvent, queryResult.error, queryResult.isFetching]);
    useEffect(function () {
        if (queryResult.status === 'pending' || queryResult.isFetching)
            return;
        setRunQuery(false);
        onSettledEvent(queryResult.data, queryResult.error);
    }, [
        onSettledEvent,
        queryResult.data,
        queryResult.error,
        queryResult.isFetching,
        queryResult.status,
        setRunQuery,
    ]);
    var search = useCallback(function (query, options) {
        if (options === void 0) { options = {}; }
        setSearchParams(function () { return ({
            query: query,
            options: options,
        }); });
        setRunQuery(query.length > 0);
    }, [setSearchParams, setRunQuery]);
    var state = useMemo(function () {
        var _a, _b;
        return (__assign(__assign({ query: query }, queryResult), { data: (_a = queryResult.data) === null || _a === void 0 ? void 0 : _a.data, total: (_b = queryResult.data) === null || _b === void 0 ? void 0 : _b.total, disableHighlight: disableHighlight }));
    }, [query, queryResult, disableHighlight]);
    return [search, state];
};
